import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, 
  User, 
  MapPin, 
  DollarSign, 
  GripVertical,
  Pencil,
  Trash2,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { format, isPast, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { STATUS_ACAO_LABELS, PRIORIDADE_ACAO_LABELS, getStatusAcaoColor, getPrioridadeColor } from '@/types/plano-acao-extended';

interface Acao5W2HCardProps {
  acao: {
    id: string;
    what_oque: string;
    why_porque: string;
    who_quem?: string | null;
    where_onde?: string | null;
    when_quando_inicio?: string | null;
    when_quando_fim?: string | null;
    how_como?: string | null;
    how_much_quanto?: number | null;
    status: string;
    prioridade?: string | null;
    percentual_conclusao?: number | null;
    motivo_bloqueio?: string | null;
    responsavel?: { id: string; name: string } | null;
  };
  onEdit?: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
}

export const Acao5W2HCard = ({ 
  acao, 
  onEdit, 
  onDelete, 
  readOnly,
  isDragging,
  dragHandleProps
}: Acao5W2HCardProps) => {
  const isOverdue = acao.when_quando_fim && isPast(new Date(acao.when_quando_fim)) && acao.status !== 'concluida';
  const daysRemaining = acao.when_quando_fim ? differenceInDays(new Date(acao.when_quando_fim), new Date()) : null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card 
      className={cn(
        "transition-all duration-200",
        isDragging && "opacity-50 shadow-lg rotate-2",
        isOverdue && acao.status !== 'concluida' && "border-red-300 bg-red-50/50",
        acao.status === 'bloqueada' && "border-orange-300 bg-orange-50/50"
      )}
    >
      <CardContent className="p-3">
        <div className="flex gap-2">
          {/* Drag Handle */}
          {!readOnly && dragHandleProps && (
            <div 
              {...dragHandleProps} 
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
            >
              <GripVertical className="h-5 w-5" />
            </div>
          )}

          <div className="flex-1 space-y-2">
            {/* Header: Title + Priority + Actions */}
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-sm leading-tight line-clamp-2">
                {acao.what_oque}
              </h4>
              <div className="flex items-center gap-1 shrink-0">
                {acao.prioridade && (
                  <Badge 
                    variant="secondary" 
                    className={cn("text-xs", getPrioridadeColor(acao.prioridade as any))}
                  >
                    {PRIORIDADE_ACAO_LABELS[acao.prioridade as keyof typeof PRIORIDADE_ACAO_LABELS]}
                  </Badge>
                )}
                {!readOnly && (
                  <>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onEdit}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDelete}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Why */}
            {acao.why_porque && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                <span className="font-medium">Por quê:</span> {acao.why_porque}
              </p>
            )}

            {/* Blocked Warning */}
            {acao.status === 'bloqueada' && acao.motivo_bloqueio && (
              <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-100 rounded px-2 py-1">
                <AlertTriangle className="h-3 w-3" />
                <span>{acao.motivo_bloqueio}</span>
              </div>
            )}

            {/* Progress */}
            {acao.percentual_conclusao !== null && acao.percentual_conclusao !== undefined && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Progresso</span>
                  <span className="font-medium">{acao.percentual_conclusao}%</span>
                </div>
                <Progress value={acao.percentual_conclusao} className="h-1.5" />
              </div>
            )}

            {/* Meta Info Row */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {/* Responsável */}
              {acao.responsavel && (
                <div className="flex items-center gap-1">
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-[10px] bg-primary/10">
                      {getInitials(acao.responsavel.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate max-w-[80px]">{acao.responsavel.name}</span>
                </div>
              )}

              {/* Data Fim */}
              {acao.when_quando_fim && (
                <div className={cn(
                  "flex items-center gap-1",
                  isOverdue && "text-red-600 font-medium"
                )}>
                  {isOverdue ? (
                    <AlertTriangle className="h-3 w-3" />
                  ) : (
                    <Calendar className="h-3 w-3" />
                  )}
                  <span>{format(new Date(acao.when_quando_fim), 'dd/MM/yy', { locale: ptBR })}</span>
                  {daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 7 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 text-yellow-600 border-yellow-300">
                      {daysRemaining}d
                    </Badge>
                  )}
                </div>
              )}

              {/* Local */}
              {acao.where_onde && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate max-w-[60px]">{acao.where_onde}</span>
                </div>
              )}

              {/* Custo */}
              {acao.how_much_quanto && acao.how_much_quanto > 0 && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  <span>
                    {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency: 'BRL',
                      notation: 'compact'
                    }).format(acao.how_much_quanto)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Acao5W2HCard;
